import { SetStateAction, useCallback, useMemo, useState } from "react";
import { useUser } from "@/hooks/use-user";
import { chargePayment } from "@/utils/charge";
import { getSignedTransactionData } from "@/utils/transactions";
import { Loader2 } from "lucide-react";
import { usePosCartGuard } from "@/providers/PosCartGuardProvider";

import type { MenuTypesWithProduct, SechUser } from "@/types/pos";

import LocationSelection from "@/components/pos/location-selection";
import MainMenu from "@/components/pos/main-menu";
import OrderConfirmation from "@/components/pos/order-confirmation";

import { PosCartProvider } from "@/hooks/pos/use-cart";
import { PosOrderProvider } from "@/hooks/pos/use-order";

export default function OrderPage() {
	const { setPosHasItems } = usePosCartGuard();

	const {
		uid,
		walletAddress,
		isConnected,
		balance,
		email,
		displayName,
		updateBalance,
		loading,
	} = useUser();

	const [selectedLocation, setSelectedLocation] = useState<{
		id: number;
		name?: string;
		details?: string;
		menuTypes?: MenuTypesWithProduct[];
		isLoading?: boolean;
	} | null>(null);
	const [showMainMenu, setShowMainMenu] = useState(false);
	const [showOrderConfirmation, setShowOrderConfirmation] = useState(false);
	const [confirmedOrder, setConfirmedOrder] = useState(null);

	// Map main app user data to SechUser shape for POS hooks
	const sechUser: SechUser | null = useMemo(() => {
		if (!uid) return null;
		return {
			uid,
			walletAddress: walletAddress || "",
			balance: typeof balance === "number" ? balance : 0,
			email: email || "",
			displayName: displayName || "",
		};
	}, [uid, walletAddress, balance, email, displayName]);

	const handleLocationSelect = useCallback(
		(location: {
			id: number;
			name?: string;
			details?: string;
			menuTypes?: MenuTypesWithProduct[];
			isLoading?: boolean;
		}) => {
			setSelectedLocation(location);
			setShowMainMenu(true);
		},
		[]
	);

	const handlePaymentSuccess = useCallback(
		(order: SetStateAction<null>) => {
			setConfirmedOrder(order);
			setShowOrderConfirmation(true);
			setPosHasItems(false);
		},
		[setPosHasItems]
	);

	// Payment execution - called by PosCartProvider when customer places an order
	const executePayment = useCallback(
		async (paymentData: any): Promise<{ success: boolean; txHash?: string; orderId?: string; error?: string }> => {
			try {
				const { orderId, total, subtotal, appliedDiscount, tax_amount, taxBreakdown, items, location, paymentMethodType, paymentMethodId } = paymentData;
				const amount = typeof total.sech === "number" ? total.sech.toFixed(5) : total.sech;

				let signature: string | undefined;
				let nonce: number | undefined;
				let forwardRequest: any;

				// TOKEN flow: get wallet signature
				if (paymentMethodType !== 'CARD' && isConnected) {
					const {
						signedTx,
						txNonce,
						transferRequest: signatureData,
					} = await getSignedTransactionData({
						userAddress: walletAddress,
						amount,
					});
					signature = signedTx;
					nonce = txNonce;
					forwardRequest = signatureData;
				}

				// Process payment through unified charge endpoint
				// Backend expects total/subtotal/tax_amount as plain USD numbers
				const json = await chargePayment({
					transactionType: 'order',
					paymentMethodType: paymentMethodType || 'TOKEN',
					amount: total.usd,
					...(paymentMethodType === 'CARD' && { paymentMethodId }),
					walletAddress,
					...(paymentMethodType !== 'CARD' && { signature, forwardRequest, nonce }),
					...(appliedDiscount && {
						discountId: appliedDiscount.discountId,
						discountCode: appliedDiscount.promoCode,
						discountAmount: appliedDiscount.calculatedAmount,
					}),
					context: {
						orderId,
						total: total.usd,
						subtotal: subtotal.usd,
						tax_amount: tax_amount.usd,
						taxBreakdown,
						items,
						location,
					},
				});

				await updateBalance();

				return {
					...json,
					orderId,
				};
			} catch (err: any) {
				return {
					success: false,
					error: err.message,
				};
			}
		},
		[isConnected, walletAddress, updateBalance]
	);

	// Show loading while user data is loading
	if (loading) {
		return (
			<div className="min-h-screen bg-[#18181B] flex items-center justify-center">
				<Loader2 className="h-6 w-6 animate-spin text-gray-400" />
			</div>
		);
	}

	// Show order confirmation
	if (showOrderConfirmation && confirmedOrder) {
		return (
			<div className="bg-[#18181B] h-screen relative overflow-x-hidden overflow-y-auto">
				<OrderConfirmation
					order={confirmedOrder}
					onBackToMenu={() => {
						setShowOrderConfirmation(false);
						setConfirmedOrder(null);
						setShowMainMenu(false);
					}}
				/>
			</div>
		);
	}

	return (
		<div className="bg-[#18181B] h-screen relative overflow-x-hidden overflow-y-auto">
			<PosOrderProvider userId={sechUser?.uid!}>
				<PosCartProvider
					sechUser={sechUser}
					executePayment={executePayment}
					onPaymentSuccess={handlePaymentSuccess}
					onCartChange={setPosHasItems}
					location={selectedLocation}
				>
					{!showMainMenu && (
						<LocationSelection onLocationSelect={handleLocationSelect} />
					)}

					{showMainMenu && selectedLocation && (
						<MainMenu
							location={selectedLocation}
							onBackToLocation={() => setShowMainMenu(false)}
						/>
					)}
				</PosCartProvider>
			</PosOrderProvider>
		</div>
	);
}
